import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {MenuItem} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import Form, {MuiRFF, Yup} from "../../../../../components/form";
import Button from "../../../../../components/base/button/button";
import {DatePickerForm} from "../../../../../components/base/date-picker";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import DateUtils from "../../../../../../core/services/date-utils";
import PartAutocomplete from "../../../../../components/text-fields/autocomplete/part-autocomplete";
import CustomerAutocomplete from "../../../../../components/text-fields/autocomplete/customer-autocomplete";
import CompetitorAutocomplete from "../../../../../components/text-fields/autocomplete/competitor-autocomplete";
import OperatorAutocomplete from "../../../../../components/text-fields/autocomplete/operator-autocomplete";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";

const filledTextFieldProps = {variant: 'filled', InputProps: {disableUnderline: true, sx: {borderRadius: 1}}};

/**
 * Quotes/invoices-style search form for competitor price reports.
 */
const CompetitorPriceReportsSearchSection = ({
                                                 searchData,
                                                 onManageCompetitors,
                                                 hiddenFields = {},
                                             }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.competitor-price-reports'});
    const {priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});

    const schema = useMemo(() => Yup.object().shape({
        startDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan(t('filters.endDate')))
            .nullable(),
        endDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan(t('filters.startDate')))
            .nullable(),
    }), [t]);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        customer: searchData.filters?.customer ?? null,
        part: searchData.filters?.part ?? null,
        competitor: searchData.filters?.competitor ?? null,
        operator: searchData.filters?.operator ?? null,
        priceLevel: (priceLevelDict ?? []).find((entry) => entry.priceLevelId === searchData.filters?.priceLevelId) ?? null,
        isChecked: searchData.filters?.isChecked === true
            ? 'checked'
            : searchData.filters?.isChecked === false
                ? 'unchecked'
                : 'all',
        startDate: searchData.filters?.startDate
            ? DateUtils.serverToUTC(searchData.filters.startDate)
            : undefined,
        endDate: searchData.filters?.endDate
            ? DateUtils.serverToUTC(searchData.filters.endDate)
            : undefined,
    }), [priceLevelDict, searchData.filters]);

    /**
     * Applies the form values as list filters, matching invoices/quotes search submit.
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            customer: values.customer ?? undefined,
            part: hiddenFields.part ? searchData.filters?.part : (values.part ?? undefined),
            competitor: values.competitor ?? undefined,
            operator: hiddenFields.operator ? searchData.filters?.operator : (values.operator ?? undefined),
            priceLevelId: hiddenFields.priceLevel
                ? searchData.filters?.priceLevelId
                : (values.priceLevel?.priceLevelId ?? undefined),
            isChecked: values.isChecked === 'checked'
                ? true
                : values.isChecked === 'unchecked'
                    ? false
                    : undefined,
            startDate: values.startDate
                ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat)
                : undefined,
            endDate: values.endDate
                ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat)
                : undefined,
        });
        searchData.setPaginationInfo((prev) => ({
            ...prev,
            currentPage: 1,
        }));
    }, [hiddenFields, searchData]);

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
        >
            {({submitting, values}) => (
                <Grid2 container spacing={2}>
                    <Grid2 xs={12}>
                        <CustomerAutocomplete
                            form
                            name={'customer'}
                            label={t('filters.customer')}
                            textFieldProps={filledTextFieldProps}
                        />
                    </Grid2>
                    {
                        !hiddenFields.part &&
                        <Grid2 xs={12} md={6}>
                            <PartAutocomplete
                                form
                                name={'part'}
                                label={t('filters.part')}
                                textFieldProps={filledTextFieldProps}
                            />
                        </Grid2>
                    }
                    <Grid2 xs={12} md={6}>
                        <CompetitorAutocomplete
                            form
                            name={'competitor'}
                            label={t('filters.competitor')}
                            freeSolo={false}
                            textFieldProps={filledTextFieldProps}
                        />
                    </Grid2>
                    {
                        !hiddenFields.operator &&
                        <Grid2 xs={12} md={6}>
                            <OperatorAutocomplete
                                form
                                name={'operator'}
                                label={t('filters.operator')}
                                textFieldProps={filledTextFieldProps}
                            />
                        </Grid2>
                    }
                    {
                        !hiddenFields.priceLevel &&
                        <Grid2 xs={12} md={6}>
                            <MuiRFF.Autocomplete
                                name={'priceLevel'}
                                options={priceLevelDict ?? []}
                                getOptionLabel={(option) => option?.title ?? ''}
                                isOptionEqualToValue={(option, value) => option?.priceLevelId === value?.priceLevelId}
                                label={t('filters.priceLevel')}
                                textFieldProps={filledTextFieldProps}
                            />
                        </Grid2>
                    }
                    <Grid2 xs={12} md={6}>
                        <MuiRFF.TextField
                            name={'isChecked'}
                            label={t('filters.checked')}
                            select
                            fullWidth
                            variant={'filled'}
                            InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                        >
                            <MenuItem value={'all'}>{t('filters.checkedAll')}</MenuItem>
                            <MenuItem value={'checked'}>{t('filters.checkedYes')}</MenuItem>
                            <MenuItem value={'unchecked'}>{t('filters.checkedNo')}</MenuItem>
                        </MuiRFF.TextField>
                    </Grid2>
                    <Grid2 xs={12} md={6}>
                        <DatePickerForm
                            name={'startDate'}
                            label={t('filters.startDate')}
                            required={required.startDate}
                            maxDate={values.endDate}
                        />
                    </Grid2>
                    <Grid2 xs={12} md={6}>
                        <DatePickerForm
                            name={'endDate'}
                            label={t('filters.endDate')}
                            required={required.endDate}
                            minDate={values.startDate}
                        />
                    </Grid2>
                    <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'center', mt: 1}}>
                        <Button
                            disabled={!!submitting}
                            type={'submit'}
                            variant={'contained'}
                            color={'primary'}
                            sx={{py: 1.5, px: 8}}
                        >
                            {t('filters.search')}
                        </Button>
                    </Grid2>
                </Grid2>
            )}
        </Form>
    );
};

export default CompetitorPriceReportsSearchSection;
